import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Unit } from 'src/components/widgets/areaDesigner/helpers';
import MdFlipToBack from 'src/shared/material_icons/action/flip_to_back.svg';
import MdFlipToFront from 'src/shared/material_icons/action/flip_to_front.svg';
import MdZoomOut from 'src/shared/material_icons/action/zoom_out.svg';
import MdZoomIn from 'src/shared/material_icons/action/zoom_in.svg';
import styled from 'styled-components';
import { Icon } from '../../widgets/icon';
import Stack from '../../widgets/stack/stack';
import { Area, Layer, PlacementItem, PlacementLayer } from './placementAreaDesigner';
import { PlacementAreaDesignerEvents, PlacementAreaDesignerManager } from './placementAreaDesignerManager';
import { PlacementTool } from './shared/sharedPlacementComponents';

const Canvas = styled.canvas`
    display: block;
    width: 100%;
    height: 100%;
`;

const AreaDesignerViewerContainer = styled.div`
    min-height: 0;
    min-width: 0;
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
`;

const AreaDesignerViewerZoomIcons = styled(Stack)`
    position: absolute;
    left: 15px;
    top: 50%;
    transform: translateY(-50%);

    ${Icon} {
        width: 32px;
        height: 32px;
        color: ${(props) => props.theme.app.primary};

        svg {
            width: 32px;
            height: 32px;
        }
    }
`;

const AreaDesignerRightSide = styled(Icon)`
    position: absolute;
    right: 15px;
    top: calc(50% - 30px);

    ${Icon} {
        display: block;
        width: 25px;
        height: 25px;
        color: ${(props) => props.theme.app.primary};
    }
`;

interface PlacementAreaDesignerViewerProps {
    unit?: Unit;

    selectedItemGuid?: string | null;
    selectedItemsGuid?: string[];
    areas: Area[];
    items: PlacementItem[];
    layers: PlacementLayer[];
    ppcm?: number;
    tool: PlacementTool;
    documentSize?: { widthCm: number; heightCm: number };

    onAreaChanged?: (item: PlacementItem, newPPCM: number | null, layers: Layer[]) => void;
    onAreaRemoved?: (area: Area) => void;
    onItemCreated?: (item: PlacementItem) => void;
}

export const PlacementAreaDesignerViewer = React.forwardRef<{ resize: () => void }, PlacementAreaDesignerViewerProps & PlacementAreaDesignerEvents>(
    (props, ref) => {
        const parentRef = useRef<HTMLDivElement>(null);
        const canvasRef = useRef<HTMLCanvasElement>(null);
        const [manager, setManager] = useState<PlacementAreaDesignerManager | null>(null);
        const [isReady, setIsReady] = useState(false);
        const [, setIsReadyCanvas] = useState(false);

        // Init
        useEffect(() => {
            if (canvasRef.current) setManager(PlacementAreaDesignerManager.create(canvasRef.current, 'PLACEMENT'));

            return () => manager?.destroy();
        }, [canvasRef.current]);

        useEffect(() => {
            setIsReady(true);
        }, []);

        useEffect(() => {
            if (isReady) setIsReadyCanvas(true);
        }, [isReady]);

        useEffect(() => {
            if (props.unit) manager?.setUnit(props.unit);
            manager?.setPlacementSize(props.documentSize?.widthCm, props.documentSize?.heightCm);
        }, [props.documentSize?.widthCm, props.documentSize?.heightCm, props.unit, manager]);

        // if (props.ppcm !== undefined)
        //     manager?.setPPCM(props.ppcm);

        // Keep data synced
        manager?.setEvents(props);
        manager?.setAreas(props.areas);
        manager?.setItems(props.items);
        manager?.setLayers(props.layers);
        manager?.setTool(props.tool);
        manager?.setSelectedItemGuid(props.selectedItemGuid);
        manager?.setSelectedAreas(props.selectedItemsGuid ?? []);
        manager?.update();

        const resize = () => {
            manager?.updateCanvasSize();
        };

        useImperativeHandle(ref, () => ({
            resize
        }));

        return (
            <AreaDesignerViewerContainer ref={parentRef}>
                {parentRef.current && <Canvas ref={canvasRef} width={parentRef.current.offsetWidth} height={parentRef.current.offsetHeight} />}
                <AreaDesignerViewerZoomIcons $gap='8px'>
                    <Icon $hoverable onClick={manager?.zoomIn}>
                        <MdZoomIn />
                    </Icon>
                    <Icon $hoverable onClick={manager?.zoomOut}>
                        <MdZoomOut />
                    </Icon>
                </AreaDesignerViewerZoomIcons>
                <AreaDesignerRightSide>
                    {props.selectedItemGuid && props.areas.find((x) => x.guid === props.selectedItemGuid) && (
                        <Icon $hoverable onClick={() => manager?.setZIndex('FRONT')}>
                            <MdFlipToFront />
                        </Icon>
                    )}
                    {props.selectedItemGuid && props.areas.find((x) => x.guid === props.selectedItemGuid) && (
                        <Icon $hoverable onClick={() => manager?.setZIndex('BACK')}>
                            <MdFlipToBack />
                        </Icon>
                    )}
                </AreaDesignerRightSide>
            </AreaDesignerViewerContainer>
        );
    }
);
